/**
 * 课程生成器页面
 * ============================================================
 *
 * 用途：内置 15 课学完后，用户按自己的需求生成新课程。
 *
 * 交互设计：
 *   ① 填主题（或点建议）→ ② 选难度 → ③ 生成 → ④ 预览 → ⑤ 直接开始上课
 *
 * 生成耗时较长（实测 30-60 秒，因为要生成 + 严格校验 + 必要时重生成），
 * 所以必须有明确的进度提示，不能让用户以为卡死了。
 * 校验不通过时把**具体问题**列出来，而不是笼统说"失败了"——
 * 用户看到「粤拼与汉字数不符」就知道可以换个说法再试。
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { getSelectedDialectId, fetchDialect } from '../lib/dialects';

const LEVEL_META = {
  beginner: { name: '初级', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  intermediate: { name: '中级', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  advanced: { name: '高级', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
};

export default function GenerateCoursePage() {
  const navigate = useNavigate();

  const [topic, setTopic] = useState('');
  const [level, setLevel] = useState('beginner');
  const [suggestions, setSuggestions] = useState([]);
  const [levels, setLevels] = useState([]);
  // 目标方言：决定生成提示词与校验规则（粤语要粤拼+繁体，其他方言不要注音+简体）
  const [dialectId, setDialectId] = useState(() => getSelectedDialectId());
  const [dialectMeta, setDialectMeta] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [lesson, setLesson] = useState(null);
  const [problems, setProblems] = useState([]);
  const [error, setError] = useState(null);

  // 拉建议主题
  useEffect(() => {
    api
      .get('/api/lesson-generate/suggestions')
      .then((res) => {
        setSuggestions(res.data?.data?.suggestions || []);
        setLevels(res.data?.data?.levels || []);
      })
      .catch(() => {});
  }, []);

  // 取当前方言的信息（显示给用户看，避免"生成的课不知道是哪个方言的"）
  useEffect(() => {
    let cancelled = false;
    fetchDialect(dialectId)
      .then((d) => {
        if (!cancelled) setDialectMeta(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [dialectId]);

  // 生成计时：让用户知道在跑，而不是卡住
  useEffect(() => {
    if (!generating) return undefined;
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [generating]);

  const generate = useCallback(async (t, lv) => {
    const useTopic = String(t ?? topic).trim();
    if (!useTopic) {
      setError('请先填写课程主题');
      return;
    }
    setGenerating(true);
    setError(null);
    setProblems([]);
    setLesson(null);
    try {
      const res = await api.post(
        '/api/lesson-generate',
        // dialect 决定生成提示词与校验规则，必须传
        { topic: useTopic, level: lv ?? level, dialect: dialectId },
        { timeout: 180000 } // 生成 + 校验 + 重试可能到 90 秒以上
      );
      setLesson(res.data?.data?.lesson || null);
    } catch (e) {
      const data = e.response?.data;
      if (data?.problems?.length) {
        // 把具体校验问题展示出来，用户才知道怎么改
        setProblems(data.problems);
        setError(data.error || '生成结果未通过质量校验');
      } else {
        setError(data?.error || e.message || '生成失败');
      }
    } finally {
      setGenerating(false);
    }
  }, [topic, level, dialectId]);

  return (
    <div className="min-h-screen bg-[#fdfaf1] pb-24 dark:bg-zinc-900">
      {/* 顶部 */}
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-zinc-100 bg-white/95 px-5 py-4 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/95">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="text-zinc-500 active:opacity-60"
          aria-label="返回"
        >
          <span className="material-icons">arrow_back</span>
        </button>
        <h1 className="flex-1 text-base font-bold text-zinc-800 dark:text-zinc-100">生成新课程</h1>
      </header>

      <main className="px-5 py-5">
        {/*
          目标方言：必须让用户看见。
          生成规则是**按方言分级**的 —— 粤语要粤拼+繁体，
          其他方言不要注音+简体。生成完才发现方言不对就要重做。
        */}
        <Link
          to="/dialects"
          className="mb-4 flex items-center gap-2 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 active:scale-[0.99]"
        >
          <span className="material-icons text-primary">translate</span>
          <span className="min-w-0 flex-1">
            <span className="block text-[11px] text-primary/70">正在为这个方言生成课程</span>
            <span className="block text-sm font-bold text-primary">
              {dialectMeta?.displayName || dialectId}
            </span>
          </span>
          <span className="text-[11px] text-primary/70">更换</span>
          <span className="material-icons text-primary/50">chevron_right</span>
        </Link>

        <p className="mb-4 text-[13px] leading-relaxed text-zinc-500 dark:text-zinc-400">
          {dialectMeta?.romanization?.required
            ? '说出你想练的场景，我来生成一整节课：目标句型（带注音）、生词、交际任务、对话角色台词，以及填空/换词练习。'
            : '说出你想练的场景，我来生成一整节课：目标句型、生词、交际任务、对话角色台词，以及填空/换词练习。（该方言暂无通用注音，所以不标拼音）'}
        </p>

        {/* 主题输入 */}
        <label className="mb-1.5 block text-xs font-bold text-zinc-500">想练什么场景？</label>
        <textarea
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          rows={2}
          placeholder="例如：去药房买感冒药"
          className="w-full resize-none rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-800 outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
        />

        {/* 建议主题 */}
        {suggestions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {suggestions.slice(0, 8).map((s) => (
              <button
                key={s.topic}
                type="button"
                onClick={() => {
                  setTopic(s.topic);
                  setLevel(s.level);
                }}
                className="rounded-full bg-white px-3 py-1.5 text-xs text-zinc-600 shadow-sm active:scale-95 dark:bg-zinc-800 dark:text-zinc-300"
              >
                {s.topic}
              </button>
            ))}
          </div>
        )}

        {/* 难度 */}
        <label className="mb-2 mt-5 block text-xs font-bold text-zinc-500">难度</label>
        <div className="flex gap-2">
          {(levels.length
            ? levels
            : [
                { id: 'beginner', name: '初级' },
                { id: 'intermediate', name: '中级' },
                { id: 'advanced', name: '高级' },
              ]
          ).map((lv) => (
            <button
              key={lv.id}
              type="button"
              onClick={() => setLevel(lv.id)}
              className={`flex-1 rounded-2xl py-2.5 text-sm font-bold transition ${
                level === lv.id
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-white text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
              }`}
            >
              {lv.name}
            </button>
          ))}
        </div>

        {/* 生成按钮 */}
        <button
          type="button"
          onClick={() => generate()}
          disabled={generating || !topic.trim()}
          className="mt-5 w-full rounded-2xl bg-primary py-3.5 text-base font-bold text-white shadow-md transition active:scale-[0.98] disabled:opacity-40"
        >
          {generating ? `正在生成… ${elapsed}s` : '生成课程'}
        </button>

        {generating && (
          <p className="mt-2 text-center text-xs text-zinc-400">
            生成后还会逐项校验粤拼与繁体，通常需要 30-60 秒
          </p>
        )}

        {/* 错误与校验问题 */}
        {error && (
          <div className="mt-4 rounded-2xl bg-amber-50 px-4 py-3 dark:bg-amber-900/25">
            <p className="flex items-center gap-1.5 text-sm font-bold text-amber-800 dark:text-amber-200">
              <span className="material-icons !text-base">info</span>
              {error}
            </p>
            {problems.length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {problems.slice(0, 6).map((p) => (
                  <li key={p} className="text-xs leading-relaxed text-amber-700 dark:text-amber-300">
                    · {p}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* 生成结果预览 */}
        {lesson && (
          <LessonPreview
            lesson={lesson}
            onStart={() => navigate(`/session/${lesson.id}`)}
            onRegenerate={() => generate()}
          />
        )}
      </main>
    </div>
  );
}

/* ============================================================
 * 生成结果预览
 * ============================================================ */
function LessonPreview({ lesson, onStart, onRegenerate }) {
  const meta = LEVEL_META[lesson.level] || LEVEL_META.beginner;

  return (
    <section className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm dark:bg-zinc-800">
      <div className="flex items-center gap-3 border-b border-zinc-100 px-4 py-3 dark:border-zinc-700">
        <span className="text-2xl">{lesson.icon}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-zinc-800 dark:text-zinc-100">{lesson.title}</p>
          <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${meta.cls}`}>
            {meta.name}
          </span>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <div>
          <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-zinc-400">学完你能</p>
          <p className="text-[13px] leading-relaxed text-zinc-600 dark:text-zinc-300">{lesson.goal}</p>
        </div>

        <div>
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-400">
            目标句型（{lesson.phrases.length} 句）
          </p>
          <div className="space-y-2">
            {lesson.phrases.map((p) => (
              <div key={p.cantonese} className="rounded-xl bg-[#f8f6f6] px-3 py-2 dark:bg-zinc-900/50">
                <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">{p.cantonese}</p>
                <p className="font-mono text-[11px] text-primary">{p.jyutping}</p>
                <p className="text-[11px] text-zinc-400">{p.mandarin}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-400">生词</p>
          <div className="flex flex-wrap gap-1.5">
            {lesson.vocab.map((v) => (
              <span
                key={v.word}
                className="rounded-full bg-zinc-100 px-2.5 py-1 text-xs text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300"
              >
                {v.word}
                <span className="ml-1 font-mono text-[10px] text-primary">{v.jyutping}</span>
              </span>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-400">交际任务</p>
          <ul className="space-y-1">
            {lesson.tasks.map((t) => (
              <li key={t.id} className="flex items-start gap-1.5 text-[13px] text-zinc-600 dark:text-zinc-300">
                <span className="material-icons !text-base shrink-0 text-primary/70">radio_button_unchecked</span>
                {t.label}
              </li>
            ))}
          </ul>
        </div>

        {lesson.variants?.length > 0 && (
          <div>
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-400">换词练习</p>
            {lesson.variants.map((v) => (
              <p key={v.from} className="text-[13px] text-zinc-600 dark:text-zinc-300">
                {v.from} → <span className="font-bold text-primary">{v.to}</span>
                {v.tip && <span className="ml-1 text-[11px] text-zinc-400">（{v.tip}）</span>}
              </p>
            ))}
          </div>
        )}

        {lesson.cultureTip && (
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-[12px] leading-relaxed text-amber-800 dark:bg-amber-900/25 dark:text-amber-200">
            💡 {lesson.cultureTip}
          </p>
        )}
      </div>

      <div className="flex gap-2 border-t border-zinc-100 p-4 dark:border-zinc-700">
        <button
          type="button"
          onClick={onRegenerate}
          className="flex-1 rounded-2xl border border-zinc-200 py-3 text-sm font-bold text-zinc-600 active:scale-[0.98] dark:border-zinc-700 dark:text-zinc-300"
        >
          重新生成
        </button>
        <button
          type="button"
          onClick={onStart}
          className="flex-1 rounded-2xl bg-primary py-3 text-sm font-bold text-white shadow-md active:scale-[0.98]"
        >
          开始上课 →
        </button>
      </div>
    </section>
  );
}
